import { useMemo } from 'react';
import { useDesignStore, type WorkspaceStep } from '@/state/store';
import { useWallMesh } from '@/ui/hooks/useWallMesh';
import { useTheme } from '@/ui/hooks/useTheme';
import { Viewport } from '@/ui/viewport/Viewport';
import { TextureStep } from '@/ui/panels/TextureStep';
import { PanelStep } from '@/ui/panels/PanelStep';
import { ExportStep } from '@/ui/panels/ExportStep';

const STEPS: ReadonlyArray<{ id: WorkspaceStep; label: string; caption: string }> = [
  { id: 'texture', label: 'Texture', caption: 'Import or generate' },
  { id: 'panel', label: 'Panel', caption: 'Size and relief' },
  { id: 'export', label: 'Export', caption: 'STL or 3MF' },
];

export default function App() {
  const design = useDesignStore((state) => state.design);
  const step = useDesignStore((state) => state.step);
  const setStep = useDesignStore((state) => state.setStep);
  const selectedPanel = useDesignStore((state) => state.selectedPanel);
  const setSelectedPanel = useDesignStore((state) => state.setSelectedPanel);
  const notices = useDesignStore((state) => state.notices);
  const dismissNotices = useDesignStore((state) => state.dismissNotices);
  const reset = useDesignStore((state) => state.reset);

  const [theme, toggleTheme] = useTheme();
  const { wall, stats, building, error, segments } = useWallMesh(design);

  // The texture step shows its own notes, so the global banner would duplicate.
  const showNotices = notices.length > 0 && step !== 'texture';
  const stepIndex = useMemo(() => STEPS.findIndex((entry) => entry.id === step), [step]);

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <svg className="brand__mark" viewBox="0 0 32 32" aria-hidden="true">
            <path
              d="M4 21c3.2-8 6.4-8 9.6 0s6.4 8 9.6 0"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
            />
            <path
              d="M4 27c3.2-8 6.4-8 9.6 0s6.4 8 9.6 0"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              opacity="0.4"
            />
          </svg>
          <div>
            <h1 className="brand__name">Organic3D</h1>
            <p className="brand__tagline">3D wall panel configurator</p>
          </div>
        </div>

        <nav className="steps" aria-label="Workflow">
          {STEPS.map((entry, index) => (
            <button
              key={entry.id}
              type="button"
              className={`step-button${step === entry.id ? ' is-active' : ''}${
                index < stepIndex ? ' is-done' : ''
              }`}
              onClick={() => setStep(entry.id)}
            >
              <span className="step-button__index">{index + 1}</span>
              <span className="step-button__text">
                <span className="step-button__label">{entry.label}</span>
                <span className="step-button__caption">{entry.caption}</span>
              </span>
            </button>
          ))}
        </nav>

        <div className="topbar__actions">
          <button
            type="button"
            className="icon-button"
            onClick={toggleTheme}
            title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
          >
            {theme === 'dark' ? '☀' : '☾'}
          </button>
          <button type="button" className="icon-button" onClick={reset} title="Start a new design">
            New
          </button>
          <a
            className="icon-button"
            href="https://github.com/LucaFrankfurt/Organic3D"
            target="_blank"
            rel="noreferrer noopener"
          >
            GitHub
          </a>
        </div>
      </header>

      <main className="workspace">
        <aside className="sidebar">
          {step === 'texture' ? <TextureStep /> : null}
          {step === 'panel' ? <PanelStep backlight={wall?.backlight ?? null} /> : null}
          {step === 'export' ? <ExportStep /> : null}
        </aside>

        <section className="stage">
          {showNotices ? (
            <div className="banner" role="status">
              <ul>
                {notices.map((notice) => (
                  <li key={notice}>{notice}</li>
                ))}
              </ul>
              <button
                type="button"
                className="icon-button icon-button--small"
                onClick={dismissNotices}
              >
                ×
              </button>
            </div>
          ) : null}

          <Viewport
            wall={wall}
            theme={theme}
            stats={stats}
            building={building}
            error={error}
            segments={segments}
            requestedSegments={design.panel.segments}
            selectedPanel={selectedPanel}
            onSelectPanel={setSelectedPanel}
          />
        </section>
      </main>
    </div>
  );
}
